import React, { useState, useEffect, useMemo } from "react";
import { Technic, Workshop } from "../types";
import { technicService } from "../services/dataService";
import "./TechnicModal.css";

interface TechnicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTechnic: (technic: Technic) => void;
  selectedTechnics?: Technic[];
  onClearAll?: () => void;
  workshops?: Workshop[]; // Atölye listesi
  onOpenCreateModal?: () => void; // Yeni teknik modal açma
}

const TechnicModal: React.FC<TechnicModalProps> = ({
  isOpen,
  onClose,
  onSelectTechnic,
  selectedTechnics = [],
  onClearAll,
  workshops = [],
  onOpenCreateModal,
}) => {
  const [technics, setTechnics] = useState<Technic[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      loadTechnics();
      setSearchTerm("");
    }
  }, [isOpen]);

  const loadTechnics = async () => {
    try {
      setLoading(true);
      const data = await technicService.getAll();
      setTechnics(data);
    } catch (error) {
      console.error("Teknikler yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const isSelected = (technic: Technic) =>
    selectedTechnics.some((st) => st.technicId === technic.technicId);

  const filteredTechnics = useMemo(() => {
    if (!searchTerm.trim()) return technics;
    const term = searchTerm.toLowerCase();
    return technics.filter(
      (t) =>
        t.name.toLowerCase().includes(term) ||
        t.category?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term),
    );
  }, [technics, searchTerm]);

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
    } else {
      selectedTechnics.forEach((t) => onSelectTechnic(t));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="technic-modal-overlay" onMouseDown={onClose}>
      <div
        className="technic-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="technic-modal-header">
          <div className="technic-modal-head-l">
            <span className="technic-modal-icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
              </svg>
            </span>
            <div>
              <h2>Teknik Seçimi</h2>
              <p>Siparişe uygulanacak teknikleri seçin</p>
            </div>
          </div>
          <button
            type="button"
            className="technic-modal-close"
            onClick={onClose}
            aria-label="Kapat"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="technic-modal-toolbar">
          <div className="technic-search-wrapper">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="technic-search-input"
              placeholder="Teknik ara... (isim, kategori, açıklama)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="technic-selected-info">
            <span className="technic-selected-count">
              <b>{selectedTechnics.length}</b> seçili
            </span>
            {selectedTechnics.length > 0 && (
              <button
                type="button"
                className="technic-clear-btn"
                onClick={handleClearAll}
              >
                Temizle
              </button>
            )}
          </div>
        </div>

        {/* Selected chips */}
        {selectedTechnics.length > 0 && (
          <div className="technic-selected-chips">
            {selectedTechnics.map((t) => (
              <span key={t.technicId} className="technic-selected-chip">
                {t.name}
                <button
                  type="button"
                  onClick={() => onSelectTechnic(t)}
                  aria-label={`${t.name} kaldır`}
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="technic-modal-body">
          {loading ? (
            <div className="technic-loading">
              <div className="technic-loading-spinner" />
              <span>Teknikler yükleniyor...</span>
            </div>
          ) : filteredTechnics.length === 0 ? (
            <div className="technic-empty">
              <div className="technic-empty-icon">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <p>
                {searchTerm
                  ? "Aramanızla eşleşen teknik bulunamadı"
                  : "Henüz teknik eklenmemiş"}
              </p>
              {!searchTerm && technics.length === 0 && onOpenCreateModal && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={onOpenCreateModal}
                >
                  İlk Tekniği Oluştur
                </button>
              )}
            </div>
          ) : (
            <div className="technic-grid">
              {filteredTechnics.map((technic) => {
                const selected = isSelected(technic);
                return (
                  <div
                    key={technic.technicId}
                    className={`technic-card ${selected ? "is-selected" : ""}`}
                    onClick={() => onSelectTechnic(technic)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectTechnic(technic);
                      }
                    }}
                  >
                    <span className="technic-card-check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <div className="technic-card-main">
                      <div className="technic-card-name">{technic.name}</div>
                      {(technic.category || technic.workshop) && (
                        <div className="technic-card-tags">
                          {technic.category && (
                            <span className="technic-card-tag">
                              {technic.category}
                            </span>
                          )}
                          {technic.workshop && (
                            <span className="technic-card-tag technic-card-tag--ws">
                              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 20h20M4 20V8l6 4V8l6 4V8l4 2v10" />
                              </svg>
                              {technic.workshop.name}
                            </span>
                          )}
                        </div>
                      )}
                      {technic.description && (
                        <p className="technic-card-desc">
                          {technic.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="technic-modal-footer">
          {onOpenCreateModal && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onOpenCreateModal}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Yeni Teknik
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Tamam · {selectedTechnics.length} seçili
          </button>
        </div>
      </div>
    </div>
  );
};

export default TechnicModal;
